import { NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { FuseConfig, FuseConfigService, Scheme } from '@fuse/services/config';
import { Subject, takeUntil } from 'rxjs';

const SCHEME_STORAGE_KEY = 'leroux-scheme';

@Component({
    selector: 'scheme-toggle',
    standalone: true,
    imports: [MatButtonModule, MatIconModule, MatTooltipModule, NgIf],
    template: `
        <button
            mat-icon-button
            [matTooltip]="isDark ? 'Passer en mode clair' : 'Passer en mode sombre'"
            (click)="toggleScheme()">
            <mat-icon
                *ngIf="isDark"
                [svgIcon]="'heroicons_outline:sun'"></mat-icon>
            <mat-icon
                *ngIf="!isDark"
                [svgIcon]="'heroicons_outline:moon'"></mat-icon>
        </button>
    `,
})
export class SchemeToggleComponent implements OnInit, OnDestroy {
    scheme: Scheme = 'dark';
    isDark: boolean = true;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    constructor(private _fuseConfigService: FuseConfigService) { }

    ngOnInit(): void {
        // Load saved scheme from localStorage or default to 'dark'
        const savedScheme = localStorage.getItem(SCHEME_STORAGE_KEY) as Scheme | null;
        if (savedScheme && (savedScheme === 'dark' || savedScheme === 'light')) {
            this.scheme = savedScheme;
            this.isDark = savedScheme === 'dark';
            this._fuseConfigService.config = { scheme: savedScheme };
        } else {
            // Default to dark mode
            this.scheme = 'dark';
            this.isDark = true;
            this._fuseConfigService.config = { scheme: 'dark' };
            localStorage.setItem(SCHEME_STORAGE_KEY, 'dark');
        }

        // Subscribe to config changes
        this._fuseConfigService.config$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((config: FuseConfig) => {
                this.scheme = config.scheme;
                // Handle 'auto' scheme by checking actual applied theme
                if (config.scheme === 'auto') {
                    this.isDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
                } else {
                    this.isDark = config.scheme === 'dark';
                }
            });
    }

    ngOnDestroy(): void {
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    toggleScheme(): void {
        // Toggle between 'light' and 'dark'
        const newScheme: Scheme = this.isDark ? 'light' : 'dark';
        this.scheme = newScheme;
        this.isDark = newScheme === 'dark';

        // Save to localStorage
        localStorage.setItem(SCHEME_STORAGE_KEY, newScheme);

        // Update Fuse config
        this._fuseConfigService.config = { scheme: newScheme };
    }
}
